import type { Address } from "viem";
import { PROVIDER } from "@woodpecker/protocol";

/** The Console's chain configuration: build-time env, overridden by an optional config.json next to index.html. */
export interface ConsoleConfig {
  chainId: number;
  rpcUrl: string;
  contractA: Address;
  wpCredit: Address;
  directory: Address;
  /** The ExtensionDirectory (D70); absent on a stack deployed before it existed. */
  extensionDirectory?: Address;
  /** The provider's handle (woodpecker@0a1b): its runner and memory are the defaults; the protocol constant is the fallback. */
  provider: string;
  networkName: string;
  fromBlock: number;
  /** The block range of one eth_getLogs request (default 10 000). */
  logChunk?: number;
  explorerTx?: string; // https://sepolia.basescan.org/tx/
  /**
   * The sandbox attributes of an extension's iframe (D68). The default keeps the frame's origin opaque: no storage,
   * no cookies, postMessage only. An embedded browser that shows nothing in an opaque frame may add
   * allow-same-origin here; the frame is then its own origin (the host's), still not the Console's.
   */
  extensionSandbox?: string;
}

export const DEFAULT_EXTENSION_SANDBOX = "allow-scripts allow-forms allow-popups";

const env = import.meta.env as Record<string, string | undefined>;

function fromEnv(): Partial<ConsoleConfig> {
  return {
    chainId: env.VITE_CHAIN_ID ? Number(env.VITE_CHAIN_ID) : undefined,
    rpcUrl: env.VITE_RPC_URL,
    contractA: env.VITE_CONTRACT_A as Address | undefined,
    wpCredit: env.VITE_WP_CREDIT as Address | undefined,
    directory: env.VITE_DIRECTORY as Address | undefined,
    extensionDirectory: env.VITE_EXTENSION_DIRECTORY as Address | undefined,
    provider: env.VITE_PROVIDER,
    networkName: env.VITE_NETWORK_NAME,
    fromBlock: env.VITE_FROM_BLOCK ? Number(env.VITE_FROM_BLOCK) : undefined,
    logChunk: env.VITE_LOG_CHUNK ? Number(env.VITE_LOG_CHUNK) : undefined,
    explorerTx: env.VITE_EXPLORER_TX,
  };
}

export async function loadConfig(): Promise<ConsoleConfig | null> {
  let file: Partial<ConsoleConfig> = {};
  try {
    const res = await fetch("./config.json", { cache: "no-store" });
    if (res.ok) file = (await res.json()) as Partial<ConsoleConfig>;
  } catch {
    /* no config.json */
  }
  const merged = {
    ...fromEnv(),
    ...Object.fromEntries(Object.entries(file).filter(([, v]) => v !== undefined && v !== "")),
  } as Partial<ConsoleConfig>;
  if (!merged.rpcUrl || !merged.contractA || !merged.wpCredit || !merged.directory) return null;
  return {
    chainId: merged.chainId ?? 84532,
    rpcUrl: merged.rpcUrl,
    contractA: merged.contractA,
    wpCredit: merged.wpCredit,
    directory: merged.directory,
    extensionDirectory: merged.extensionDirectory,
    provider: merged.provider ?? PROVIDER,
    networkName: merged.networkName ?? "Base Sepolia",
    fromBlock: merged.fromBlock ?? 0,
    logChunk: merged.logChunk,
    explorerTx: merged.explorerTx,
  };
}
